import _ from 'lodash'
import type {
    ApplicationsMap,
    ApplicationsManager,
    RawComponents,
    RawComponentExportStructure
} from '@wix/document-services-types'

/**
 * Declares an instance of managing application dependencies and
 * the corresponding component ids that require the necessary application.
 */
export class ApplicationsManagerImpl implements ApplicationsManager {
    constructor(public applications: ApplicationsMap = {}) {}

    public addApplication(applicationId: string, componentType: string, componentId: string): void {
        this.applications[applicationId] ??= {}
        this.applications[applicationId][componentType] ??= []

        if (!_.includes(this.applications[applicationId][componentType], componentId)) {
            this.applications[applicationId][componentType].push(componentId)
        }
    }

    public getApplications(): ApplicationsMap {
        return this.applications
    }

    /**
     * For the given components, returns a map of required applicationId to corresponding
     * componentIds that require it. Requirement is derived from the source applications map
     * that was determined during the import process.
     */
    public getRequiredAppIdsByComponents(components: RawComponents): Record<string, string[]> {
        const requiredAppIds: Record<string, string[]> = {}
        const targetComponentIdsByType: Record<string, string[]> = _.reduce(
            _.values(components),
            (acc: Record<string, string[]>, {structure}: RawComponentExportStructure) => {
                const {componentType, id} = structure
                acc[componentType] ??= []
                acc[componentType].push(id)
                return acc
            },
            {}
        )

        _.forOwn(this.applications, (sourceComponentIdsByType, applicationId) => {
            let aggregatedIds: string[] = []

            _.forOwn(sourceComponentIdsByType, (_sourceIds, type) => {
                const componentIds = targetComponentIdsByType[type]
                if (componentIds && componentIds.length > 0) {
                    aggregatedIds = _.union(aggregatedIds, componentIds)
                }
            })

            if (aggregatedIds.length > 0) {
                requiredAppIds[applicationId] = aggregatedIds
            }
        })

        return requiredAppIds
    }
}
